// src/pages/Checkout.js - WITH PAYMENT SERVICE INTEGRATION
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import paymentService from '../services/payment-service';

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  
  // Get checkout data from navigation state
  const checkoutData = location.state || {
    type: 'course',
    item: {
      title: 'Intuitive Development Mastery',
      instructor: 'Sarah Moonweaver',
      price: 299,
      image: '🌟'
    }
  };

  // Determine if this is a course or session booking
  const isSession = checkoutData.type === 'session';
  
  // Sample session data if coming from practitioner booking
  const sessionDetails = isSession ? {
    practitioner: checkoutData.practitioner || { name: 'Sarah Moon', image: '👩‍🦰' },
    service: checkoutData.service || { title: '60-Minute Deep Dive Session', price: 120 },
    date: checkoutData.date || new Date(),
    time: checkoutData.time || '2:00 PM'
  } : null;

  // Form data
  const [formData, setFormData] = useState({
    email: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardName: '',
    country: 'United States',
    zipCode: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // NEW: Integrated payment processing
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setPaymentError('');

    try {
      // Prepare product data for payment service
      const product = {
        name: isSession ? sessionDetails.service.title : checkoutData.item.title,
        price: (isSession ? sessionDetails.service.price : checkoutData.item.price) * 100, // Convert to cents
        type: isSession ? 'session' : 'course',
        id: checkoutData.item.id || `${checkoutData.type}_${Date.now()}`
      };

      console.log('Processing payment for:', product);

      // Check if we're using test mode or real Stripe
      if (paymentService.isTestMode()) {
        // LocalStorage mode - simulate payment
        const result = await paymentService.processPayment(product);
        
        if (result.success) {
          // Success! Navigate to confirmation
          navigate('/checkout/success', { 
            state: { 
              ...checkoutData,
              purchase: result.purchase,
              orderNumber: 'DF-' + Math.random().toString(36).substr(2, 9).toUpperCase()
            } 
          });
        } else {
          setPaymentError(result.error || 'Payment failed. Please try again.');
        }
      } else {
        // Real Stripe mode - will redirect to Stripe Checkout
        const result = await paymentService.processPayment(product);
        
        if (result.redirecting) {
          // Stripe is redirecting, show a message
          setPaymentError(''); // Clear any errors
          // The page will redirect automatically
        } else if (!result.success) {
          setPaymentError(result.error || 'Unable to process payment.');
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError('An unexpected error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Calculate pricing
  const subtotal = isSession ? sessionDetails.service.price : checkoutData.item.price;
  const discount = promoCode ? subtotal * 0.1 : 0; // 10% discount for demo
  const tax = (subtotal - discount) * 0.08; // 8% tax for demo
  const total = subtotal - discount + tax;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="text-slate-600 hover:text-slate-900"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-slate-900">Secure Checkout</h1>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span className="text-green-600">🔒</span>
              <span>Secure SSL Encryption</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Payment Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information */}
              <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Contact Information</h2>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your@email.com"
                    disabled={isProcessing}
                  />
                  <p className="text-sm text-slate-600 mt-1">
                    {isSession ? 
                      'Session details will be sent to this email' : 
                      'Course access will be sent to this email'}
                  </p>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Payment Method</h2>
                
                {/* NEW: Test Mode Banner */}
                {paymentService.isTestMode() && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      🧪 <strong>Test Mode:</strong> Using simulated payments. No real charges will occur.
                    </p>
                  </div>
                )}
                
                {/* Payment Options */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    disabled={isProcessing}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      paymentMethod === 'card' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="text-center">
                      <span className="text-2xl">💳</span>
                      <p className="text-sm font-medium mt-1">Card</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('paypal')}
                    disabled={isProcessing}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      paymentMethod === 'paypal' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="text-center">
                      <span className="text-2xl">🅿️</span>
                      <p className="text-sm font-medium mt-1">PayPal</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('apple')}
                    disabled={isProcessing}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      paymentMethod === 'apple' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="text-center">
                      <span className="text-2xl">🍎</span>
                      <p className="text-sm font-medium mt-1">Apple Pay</p>
                    </div>
                  </button>
                </div>

                {/* Card Form */}
                {paymentMethod === 'card' && (
                  <div className="space-y-4">
                    {paymentService.isTestMode() ? (
                      // Simplified form for test mode
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-800 mb-2">
                          In test mode, card details are optional. Click "Complete Purchase" to simulate payment.
                        </p>
                        <p className="text-xs text-blue-600">
                          When Stripe is activated, you'll be redirected to secure Stripe Checkout.
                        </p>
                      </div>
                    ) : (
                      // Show message about Stripe redirect
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm text-green-800">
                          You'll be securely redirected to Stripe Checkout to complete your payment.
                        </p>
                      </div>
                    )}

                    {/* Keep form fields for visual consistency but make optional in test mode */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Card Number {paymentService.isTestMode() && <span className="text-slate-400">(optional)</span>}
                      </label>
                      <input
                        type="text"
                        name="cardNumber"
                        required={!paymentService.isTestMode()}
                        value={formData.cardNumber}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="1234 5678 9012 3456"
                        disabled={isProcessing}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Expiry Date {paymentService.isTestMode() && <span className="text-slate-400">(optional)</span>}
                        </label>
                        <input
                          type="text"
                          name="expiryDate"
                          required={!paymentService.isTestMode()}
                          value={formData.expiryDate}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="MM/YY"
                          disabled={isProcessing}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          CVV {paymentService.isTestMode() && <span className="text-slate-400">(optional)</span>}
                        </label>
                        <input
                          type="text"
                          name="cvv"
                          required={!paymentService.isTestMode()}
                          value={formData.cvv}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="123"
                          disabled={isProcessing}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {paymentMethod === 'paypal' && (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">🅿️</div>
                    <p className="text-slate-600 mb-4">You will be redirected to PayPal to complete your purchase.</p>
                    <p className="text-sm text-slate-500">PayPal integration coming soon!</p>
                  </div>
                )}

                {paymentMethod === 'apple' && (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">🍎</div>
                    <p className="text-slate-600 mb-4">Use Touch ID or Face ID to complete your purchase.</p>
                    <p className="text-sm text-slate-500">Apple Pay integration coming soon!</p>
                  </div>
                )}
              </div>

              {/* Terms & Conditions */}
              <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 mt-0.5"
                    disabled={isProcessing}
                  />
                  <span className="text-sm text-slate-700">
                    I agree to the Terms of Service and Privacy Policy. 
                    {isSession ? 
                      ' I understand that sessions are non-refundable once booked.' : 
                      ' I understand that I will have immediate access to the course content.'}
                  </span>
                </label>
              </div>

              {/* Error Message */}
              {paymentError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{paymentError}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!agreeToTerms || isProcessing}
                className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all flex items-center justify-center gap-2 ${
                  agreeToTerms && !isProcessing
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-slate-400 cursor-not-allowed'
                }`}
              >
                {isProcessing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    🔒 Complete Purchase - ${total.toFixed(2)}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Right: Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200 sticky top-8">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Order Summary</h2>
              
              {/* Item Details */}
              <div className="pb-4 border-b border-slate-200">
                {isSession ? (
                  <div className="flex gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex items-center justify-center text-2xl">
                      {sessionDetails.practitioner.image}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">{sessionDetails.service.title}</h3>
                      <p className="text-sm text-slate-600">with {sessionDetails.practitioner.name}</p>
                      <p className="text-sm text-slate-600 mt-1">
                        {sessionDetails.date.toLocaleDateString()} at {sessionDetails.time}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center text-2xl">
                      {checkoutData.item.image}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">{checkoutData.item.title}</h3>
                      <p className="text-sm text-slate-600">by {checkoutData.item.instructor}</p>
                      <p className="text-sm text-slate-600 mt-1">Lifetime access</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Promo Code */}
              <div className="py-4 border-b border-slate-200">
                {!showPromoInput ? (
                  <button
                    type="button"
                    onClick={() => setShowPromoInput(true)}
                    className="text-blue-600 text-sm font-medium hover:text-blue-700"
                    disabled={isProcessing}
                  >
                    Add promo code
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      placeholder="Enter code"
                      disabled={isProcessing}
                    />
                    <button
                      type="button"
                      onClick={() => setPromoCode(promoCode)}
                      className="px-4 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800"
                      disabled={isProcessing}
                    >
                      Apply
                    </button>
                  </div>
                )}
                {promoCode && (
                  <p className="text-sm text-green-600 mt-2">✓ Promo code applied: {promoCode}</p>
                )}
              </div>

              {/* Pricing Breakdown */}
              <div className="py-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="text-slate-900">${subtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Discount</span>
                    <span className="text-green-600">-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Tax</span>
                  <span className="text-slate-900">${tax.toFixed(2)}</span>
                </div>
                <div className="pt-2 border-t border-slate-200">
                  <div className="flex justify-between">
                    <span className="font-semibold text-slate-900">Total</span>
                    <span className="font-bold text-xl text-slate-900">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Security Badges */}
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center justify-center gap-4 text-sm text-slate-600">
                  <span>🔒 SSL</span>
                  <span>✓ PCI</span>
                  <span>🛡️ Secure</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;